<template>
    <div v-if="!canRead" class="area-content-body">
        <div class="no-permission-message">
            Sie haben keine Berechtigung, die Stammdaten zu öffnen.
        </div>
    </div>

    <div v-else class="area-content-body main-data-organization-contain">
        <dx-text-box v-model:value="searchString" :show-clear-button="true" placeholder="Volltext Suche..."></dx-text-box>

        <div v-if="searchString" class="main-data-search-container">
            <div v-for="organization in searchResults" :key="organization._id" class="main-data-search-organization" @click.prevent.stop="selectOrganization(organization)">
                <div class="main-data-search-item-bar"><span class="material-symbols-outlined">business</span><span>{{organization.name}}</span></div>
                <div v-if="organization.eMail" class="main-data-search-item-bar"><span class="material-symbols-outlined">mail</span><span>{{organization.eMail}}</span></div>
                <div v-for="contact in organization.contacts" :key="contact._id" class="main-data-search-contact" @click.prevent.stop="selectContact(contact)">
                    <div class="main-data-search-item-bar"><span class="material-symbols-outlined">person</span><span>{{contact.firstName||""}} {{contact.lastName||""}}</span></div>
                    <div v-if="contact.eMail" class="main-data-search-item-bar"><span class="material-symbols-outlined">mail</span><span>{{contact.eMail}}</span></div>
                    <div v-if="contact.phoneNumber" class="main-data-search-item-bar"><span class="material-symbols-outlined">phone</span><span>{{contact.phoneNumber}}</span></div>
                    <div v-if="contact.mobileNumber" class="main-data-search-item-bar"><span class="material-symbols-outlined">smartphone</span><span>{{contact.mobileNumber}}</span></div>
                </div>
            </div>
        </div>

        <div v-else-if="duplicates && duplicates.length" style="display:grid; grid-template-rows: max-content minmax(0,1fr);">
            <dx-button @click="duplicates = null;" icon="close"></dx-button>
            <div style="overflow: auto;">
                <div v-for="duplicate of duplicates" :key="duplicate.org._id" class="main-data-search-organization" @click.prevent.stop="selectOrganization(duplicate.org)">
                    <div class="main-data-search-item-bar"><span class="material-symbols-outlined">business</span><span>{{duplicate.org.name}}</span></div>
                    <div class="main-data-search-item-bar"><span class="material-symbols-outlined">file_copy</span><span>Duplikate: {{duplicate.count}}</span></div>
                </div>
            </div>
        </div>

        <DxDataGrid v-else
                    ref="grid"
                    class="main-data-organizations-grid"
                    :focused-row-enabled="true"
                    :auto-navigate-to-focused-row="autoNavigateToFocusedRow"
                    key-expr="_id"
                    @editing-start="organization=null"
                    :data-source="organizations"
                    v-model:focused-row-key="focusedRowKey"
                    @saving="saving"
                    :columnChooser="gridColumnChooser"
                    :stateStoring="gridStateStoring"
                    :scrolling="{mode:'virtual',preloadEnabled:true}"
                    :pager="{showInfo:true}"
                    @selection-changed="selectionChanged">
            <DxFilterRow :visible="true"></DxFilterRow>

            <DxColumn data-field="name" caption="Firma" />
            <DxColumn data-field="zipCode" caption="PLZ" :visible="false" />
            <DxColumn data-field="city" caption="Ort" />
            <DxColumn data-field="streetAndNr" caption="Straße" :visible="false" />
            <DxColumn data-field="eMail" caption="E-Mail" :visible="false" />

            <DxSelection mode="single" />

            <DxEditing :allow-adding="canWrite"                       
                       :allow-deleting="canDelete"
                       new-row-position="last"
                       :use-icons="true"
                       mode="cell" />

            <DxToolbar>
                <DxItem location="before" template="exportDataTemplate" />
                <DxItem location="before" template="scanDupeDataTemplate" />
                <DxItem v-if="canWrite" name="addRowButton"></DxItem>
                <DxItem name="columnChooserButton" />
            </DxToolbar>

            <template #exportDataTemplate>
                <DxButton text="Export" @click="exportData" :disabled="!canExport" />
            </template>

            <template #scanDupeDataTemplate>
                <DxButton text="Duplikatsprüfung" @click="scanDuplicates" :disabled="!canRead" />
            </template>
        </DxDataGrid>

        <router-view v-show="organization" v-slot="{ Component }" class="main-data-data">
            <transition name="fade">
                <component ref="organization" :is="Component" />
            </transition>
        </router-view>
    </div>
</template>

<script lang="js">
    import { defineComponent } from 'vue';
    import { parse } from '@tinyhttp/content-disposition'
    import DxDataGrid, {
        DxColumn,
        DxSelection,
        DxEditing,
        DxHeaderFilter,
        DxSearchPanel,
        DxFilterRow,
        DxToolbar,
        DxItem
    } from 'devextreme-vue/data-grid';
    import listApi from '../services/ListApi';
    import DxSearch from 'devextreme-vue/data-grid';
    import organizationApi from '../services/OrganizationApi';
    import { DxTextBox } from 'devextreme-vue';
    import { DxButton } from 'devextreme-vue/button';
    import { cleanString, getDupeCheckFilters } from '../services/Util';
    import { confirm } from 'devextreme/ui/dialog';
    import notify from 'devextreme/ui/notify';
    import meApi from '../services/MeApi';
    import AppPermissions from '../constants/AppPermissions';

    export default defineComponent({
        components: {
            DxDataGrid,
            DxSearch,
            DxColumn,
            DxSelection,
            DxSearchPanel,
            DxHeaderFilter,
            DxFilterRow,
            DxEditing,
            DxTextBox,
            DxToolbar,
            DxItem,
            DxButton
        },
        data() {
            return {
                organizations: [],
                organization: null,
                searchString: null,
                searchResults: [],
                duplicates: null,
                gridStateStoring: this.getStateStoring(),
                gridColumnChooser: { enabled: true }
            };
        },
        computed: {
            canRead() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Read);
            },
            canWrite() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Write);
            },
            canDelete() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Delete);
            },
            canExport() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Export);
            }
        },
        watch: {
            organization() {
                if (this.organization) {
                    this.$router.push("/MainData/Organizations/" + this.organization)
                }
            },
            async searchString() {
                if (!this.canRead)
                    return;

                if (this.searchString) {
                    this.searchResults = [];
                    let result = await organizationApi.search("**:" + this.searchString);
                    for (let organization of result.organizations) {
                        this.searchResults.push(organization);
                        organization.contacts = result.contacts.filter(c => c.organizationId == organization._id);
                    }
                }
            }
        },
        async created() {
            if (!this.canRead)
                return;

            await this.fetchData();
        },
        methods: {
            async fetchData() {
                if (!this.canRead)
                    return;

                this.organizations = await organizationApi.getAll();
                if (this.$route.params?.id) {
                    this.organization = this.$route.params?.id
                } else if (this.organizations.length > 0) {
                    this.organization = this.organizations[0]._id
                }
            },
            async scanDuplicates() {
                if (!this.canRead)
                    return;

                if (!await confirm("Der Duplikats-Scan kann einige Minuten in Anspruch nehmen!", "Duplikate prüfen?"))
                    return;

                let dupes = []
                let count = this.organizations.length;
                let i = 0;
                for (let org of this.organizations) {
                    i++;
                    notify(`Fortschritt Duplikatsprüfung: ${Math.round(i / count * 100)} %`);
                    let name = org.name;
                    if (!cleanString(name)) {
                        continue;
                    }
                    let searchStrings = getDupeCheckFilters(name, org.city);
                    let filteredResults = await Promise.all(searchStrings.map(s => organizationApi.search(s).then(r => r.organizations.filter(o => o._id !== org._id)))).then(m => m.flat());
                    if (filteredResults.length) {
                        dupes.push({
                            org: org,
                            count: filteredResults.length
                        })
                    }
                }
                this.duplicates = dupes;
            },
            selectOrganization(organization) {
                if (organization._id)
                    this.$router.push("/MainData/Organizations/" + organization._id);
            },
            selectContact(contact) {
                if (contact.organizationId && contact._id)
                    this.$router.push("/MainData/Organizations/" + contact.organizationId + "/" + contact._id)
            },
            async selectionChanged(e) {
                this.organization = e.selectedRowKeys[0];
            },
            getStateStoring() {
                let key = "organizations-grid:" + this.$root.currentUser;
                return {
                    enabled: true,
                    type: "custom",
                    async customLoad() {
                        let settingsList = await listApi.getByName("sys_settings");
                        let item = await listApi.getListItemByValue(settingsList._id, key);
                        if (item)
                            return JSON.parse(item.data);
                        return null;
                    },
                    async customSave(e) {
                        let settingsList = await listApi.getByName("sys_settings");
                        let item = await listApi.getListItemByValue(settingsList._id, key);
                        if (item) {
                            let prev = item.data
                            item.data = JSON.stringify({ columns: e.columns });
                            if (prev == item.data) {
                                return item;
                            }
                            await listApi.saveItem(item);
                        }
                        else {
                            return await listApi.saveItem({ listId: settingsList._id, value: key, data: JSON.stringify({ columns: e.columns }) });
                        }
                    }
                }
            },
            async saving(e) {
                if (!this.canWrite && !this.canDelete) {
                    e.cancel = true;
                    return;
                }

                for (let change of e.changes) {
                    let item = this.organizations.find(t => t._id === change.key);

                    if (change.type === "update") {
                        if (!this.canWrite) {
                            e.cancel = true;
                            return;
                        }
                        Object.assign(item, change.data);
                        await organizationApi.save(item);
                    }
                    else if (change.type === "insert") {
                        if (!this.canWrite) {
                            e.cancel = true;
                            return;
                        }
                        item = change.data;
                        item = await organizationApi.save(item);
                        this.organization = item._id;
                    }
                    else if (change.type === "remove") {
                        if (!this.canDelete) {
                            e.cancel = true;
                            return;
                        }
                        await organizationApi.delete(change.key)
                    }
                    else {
                        console.error("DONT KNOW", change.type)
                    }
                }

                if (e.changes.some(c => c.type === 'insert')) {
                    this.organizations = await organizationApi.getAll();
                }
            },
            async exportData() {
                if (!this.canExport)
                    return;

                let exportResult = await organizationApi.export();

                this.$root.toast("Stammdaten Export", [{
                    icon: "download",
                    hint: "Exceldatei herunterladen",
                    onClick: async () => {
                        var a = document.createElement("a");
                        document.body.appendChild(a);
                        a.style.display = "none";
                        a.download = parse(exportResult.headers['content-disposition']).parameters.filename;
                        a.href = URL.createObjectURL(exportResult.data);
                        a.click();
                        a.remove();
                    }
                }], 5000);
            }
        },
    });
</script>

<style>
    .main-data-organization-contain {
        display: grid;
        grid-template-columns: 1fr 3fr;
        grid-template-rows: max-content minmax(0,1fr);
        grid-gap: 10px;
        grid-auto-flow: column;
    }

    .main-data-organizations-grid {
        padding: 10px;
        background: #2a2a2a;
    }

    .main-data-data {
        grid-row: 1/span 2;
    }

    .main-data-search-organization {
        border: 1px solid var(--color-background-light);
        border-radius: var(--default-border-radius);
        padding: 5px 10px;
        margin: 10px 0;
    }

    .main-data-search-item-bar {
        display: grid;
        grid-template-columns: max-content 1fr;
        align-items: center;
        grid-gap: 10px;
    }

    .main-data-search-contact {
        padding: 5px;
        margin: 5px 0;
        border-radius: var(--default-border-radius);
        background: var(--color-background-light);
    }

    .main-data-search-container {
        overflow: auto;
        padding-right: 10px;
    }

    .no-permission-message {
        padding: 20px;
        background-color: #2a2a2a;
    }
</style>